import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ExternalLink, ChevronRight, Tv } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { ListenButton } from "../components/ListenButton";
import { useTranslate } from "../context/TranslateContext";
import { getLandmarkSerial } from "../mock/articles";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] } }),
};

export default function LandmarkSerial() {
  const { slug } = useParams();
  const { t } = useTranslate();
  const s = getLandmarkSerial(slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!s) {
    return (
      <div className="min-h-screen bg-paper">
        <InnerNav />
        <div className="mx-auto max-w-2xl px-6 py-32 text-center">
          <h1 className="font-display text-4xl text-cream">{t("Serial not found")}</h1>
          <Link to="/article/serials-overview" className="mt-6 inline-block font-body text-gold hover:underline">{t("Back to TV Serials")}</Link>
        </div>
      </div>
    );
  }

  const facts = [
    { k: "First aired", v: s.year },
    { k: "Channel", v: s.channel },
    { k: "Language", v: t(s.language) },
    { k: "Genre", v: t(s.genre) },
  ];

  return (
    <div className="min-h-screen bg-paper" data-testid="landmark-serial-page">
      <InnerNav />

      <section className="relative overflow-hidden border-b border-edge">
        <div className="absolute inset-0 opacity-90" style={{ background: `linear-gradient(135deg, ${s.accent}, ${s.accent}11)` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/50 via-paper/80 to-paper" />
        <div className="relative mx-auto max-w-3xl px-6 py-16 md:py-20">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.28em] text-gold">
              <Tv className="h-4 w-4" /> {t("Landmark Serial")} · {s.year}
            </div>
            <h1 data-testid="landmark-title" className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              {s.title}
            </h1>
            <p className="mt-5 font-body text-lg leading-relaxed text-ash md:text-xl">{t(s.tagline)}</p>
            <div className="mt-7">
              <ListenButton text={`${s.title}. ${s.tagline} ${s.note}`} testId="landmark-listen" label="Listen" sourceId={`landmark-${slug}`} sourceLabel={s.title} />
            </div>
          </motion.div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 py-14 md:py-16">
        <div className="mb-10 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="landmark-facts">
          {facts.map((f) => (
            <div key={f.k} className="rounded-2xl border border-edge bg-surface/40 p-4">
              <div className="font-body text-xs uppercase tracking-widest text-ash">{t(f.k)}</div>
              <div className="mt-1 font-display text-lg text-cream">{f.v}</div>
            </div>
          ))}
        </div>

        <p className="font-body text-lg leading-relaxed text-cream md:text-xl md:leading-loose">{t(s.note)}</p>

        <a href={s.wiki.url} target="_blank" rel="noopener noreferrer" data-testid="landmark-wiki"
          className="group mt-12 block rounded-2xl border border-edge bg-surface/40 p-6 transition-colors hover:border-gold">
          <div className="flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-ash">
            <BookOpen className="h-4 w-4" /> {t("From Wikipedia")}
          </div>
          <div className="mt-2 font-display text-xl text-cream">{s.wiki.title}</div>
          <span className="mt-3 inline-flex items-center gap-1.5 font-body text-sm text-gold group-hover:underline">
            {t("Read more on Wikipedia")} <ExternalLink className="h-3.5 w-3.5" />
          </span>
        </a>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="/article/serials-overview" data-testid="landmark-back"
            className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
            <ChevronRight className="h-4 w-4 rotate-180" /> {t("Back to TV Serials")}
          </Link>
          <Link to="/tv-serials" data-testid="landmark-directory"
            className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 font-body text-sm font-medium text-ink transition-transform hover:scale-105">
            {t("Browse serials by language")} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </article>
    </div>
  );
}
